import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  ArrowForward as ArrowForwardIcon,
  BarChart as ChartIcon,
  CheckCircle as CheckCircleIcon,
  FlashOn as FlashIcon,
  HeadsetMic as SupportIcon,
  Lock as LockIcon,
  Paid as PaidIcon,
  Security as ShieldIcon,
  Star as StarIcon,
  VerifiedUser as VerifiedIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchAppConfig, fetchPlans } from '../services/publicApi';
import { Plan } from '../types';

const sectionPx = { xs: 2.5, sm: 4, md: 6 };
const maxWidth = 1160;
const contentWidth = 1040;
const containedSx = (width: number = contentWidth) => ({
  width: '100%',
  maxWidth: width,
  mx: 'auto',
  px: sectionPx,
  boxSizing: 'border-box',
});
const gridContainerSx = { width: '100%', m: 0 };

const Badge: React.FC<{ label: string }> = ({ label }) => (
  <Chip
    size="small"
    label={label}
    sx={{
      width: 'fit-content',
      bgcolor: 'rgba(255,201,40,0.12)',
      border: '1px solid rgba(255,201,40,0.45)',
      color: '#9a6a00',
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 0.7,
      height: 24,
    }}
  />
);

const HeroRideCard: React.FC<{
  from: string;
  to: string;
  time: string;
  price: string;
  muted?: boolean;
}> = ({ from, to, time, price, muted }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.75, sm: 2.25 },
      borderRadius: 1.5,
      border: '1px solid rgba(255,255,255,0.72)',
      bgcolor: 'rgba(255,255,255,0.94)',
      boxShadow: '0 18px 55px rgba(2, 14, 28, 0.18)',
    }}
  >
    <Stack spacing={1.25}>
      <Stack direction="row" justifyContent="space-between" gap={{ xs: 1, sm: 2 }} alignItems="flex-start">
        <Box minWidth={0} sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ fontSize: 10 }}>
            PICKUP
          </Typography>
          <Typography variant="body2" fontWeight={800} noWrap>
            {from}
          </Typography>
        </Box>
        <ArrowForwardIcon sx={{ color: '#d99b00', fontSize: 18, mt: 2, flexShrink: 0 }} />
        <Box minWidth={0} textAlign="right" sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ fontSize: 10 }}>
            DROP
          </Typography>
          <Typography variant="body2" fontWeight={800} noWrap>
            {to}
          </Typography>
        </Box>
      </Stack>
      <Divider />
      <Stack direction="row" justifyContent="space-between" gap={1}>
        <Typography variant="caption" color="text.secondary">
          {time}
        </Typography>
        <Typography variant="body2" fontWeight={900} color={muted ? 'text.secondary' : 'success.main'}>
          {price}
        </Typography>
      </Stack>
    </Stack>
  </Paper>
);

const FeatureTile: React.FC<{ icon: React.ReactNode; title: string; body: string }> = ({ icon, title, body }) => (
  <Paper
    variant="outlined"
    sx={{
      p: { xs: 2, sm: 2.25 },
      borderRadius: 1.5,
      height: '100%',
      borderColor: 'rgba(7,24,45,0.09)',
      boxShadow: '0 12px 30px rgba(7,24,45,0.035)',
      transition: 'transform .2s ease, box-shadow .2s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 18px 42px rgba(7,24,45,0.08)',
      },
    }}
  >
    <Stack spacing={1.25}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: 1.25,
          bgcolor: '#fff4c7',
          color: '#9a6a00',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" fontWeight={900}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" lineHeight={1.65}>
        {body}
      </Typography>
    </Stack>
  </Paper>
);

const Step: React.FC<{ n: string; title: string; body: string; icon: React.ReactNode }> = ({ n, title, body, icon }) => (
  <Stack alignItems="center" textAlign="center" spacing={1.25}>
    <Box
      sx={{
        width: 58,
        height: 58,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.18)',
        bgcolor: 'rgba(255,255,255,0.06)',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <Typography variant="subtitle2" color="#ffc928" fontWeight={900}>
        {n}
      </Typography>
    </Box>
    <Box sx={{ color: 'white', height: 24 }}>{icon}</Box>
    <Typography variant="subtitle2" color="white" fontWeight={900}>
      {title}
    </Typography>
    <Typography variant="caption" color="rgba(255,255,255,0.7)" lineHeight={1.7} maxWidth={180}>
      {body}
    </Typography>
  </Stack>
);

const Landing: React.FC = () => {
  const { token } = useAuth();
  const isLoggedIn = Boolean(token);
  const ridesPath = isLoggedIn ? '/app/rides' : '/rides';
  const [signupFreeCredits, setSignupFreeCredits] = React.useState<number>(5);
  const [plans, setPlans] = React.useState<Plan[]>([]);

  React.useEffect(() => {
    fetchAppConfig().then((cfg) => setSignupFreeCredits(cfg.signupFreeCredits)).catch(() => {});
    fetchPlans().then(setPlans).catch(() => {});
  }, []);

  const features = [
    { icon: <FlashIcon fontSize="small" />, title: 'Real-time Rides', body: 'Get live ride requirements from authorized sources and verified contributors.' },
    { icon: <LockIcon fontSize="small" />, title: 'Ride Unlocks', body: 'Buy credits to unlock rides. Credits are used only when you unlock a ride.' },
    { icon: <VerifiedIcon fontSize="small" />, title: 'Trip Details', body: 'Route, timing and contact visibility where applicable before you commit.' },
    { icon: <ChartIcon fontSize="small" />, title: 'Fair Dashboard', body: 'Track rides, quota, referrals, performance and outcomes.' },
    { icon: <ShieldIcon fontSize="small" />, title: 'Focused Feed', body: 'Spam, duplicates and expired listings are reviewed to keep the feed useful.' },
    { icon: <PaidIcon fontSize="small" />, title: 'Earn More', body: 'Better trip visibility means less idle time and more confirmed rides.' },
    { icon: <SupportIcon fontSize="small" />, title: 'Route Coverage', body: 'Delhi NCR, Jaipur, Chandigarh and more corridors expanding.' },
    { icon: <StarIcon fontSize="small" />, title: 'Flexible Plans', body: 'Free monthly credits, Rs 79 and Rs 149 plans, plus top-ups.' },
  ];

  const stats = [
    { value: 'Free', label: 'Monthly Credits' },
    { value: '₹79', label: 'Starter Plan' },
    { value: '₹149', label: 'Pro Plan' },
    { value: 'Live', label: 'Authorized Rides' },
  ];

  return (
    <Box sx={{ bgcolor: '#f5f7fb', overflowX: 'hidden' }}>
      <Box
        sx={{
          bgcolor: '#061b31',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 6, md: 8 },
          pb: { xs: 10, md: 14 },
          backgroundImage:
            'linear-gradient(135deg, rgba(6,27,49,1) 0%, rgba(8,35,63,1) 54%, rgba(3,20,39,1) 100%), linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: 'auto, 72px 72px, 72px 72px',
        }}
      >
        <Box sx={containedSx(maxWidth)}>
          <Grid
            container
            rowSpacing={{ xs: 4, md: 8 }}
            columnSpacing={{ xs: 0, md: 6 }}
            alignItems="center"
            justifyContent="center"
            sx={gridContainerSx}
          >
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Chip
                size="small"
                icon={<FlashIcon sx={{ fontSize: 14, color: '#ffc928 !important' }} />}
                label="LIVE, FAIR & TRANSPARENT"
                sx={{
                  width: 'fit-content',
                  bgcolor: 'rgba(255,201,40,0.14)',
                  border: '1px solid rgba(255,201,40,0.4)',
                  color: '#ffc928',
                  fontSize: 11,
                  fontWeight: 900,
                }}
              />
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: 30, sm: 48, md: 60 },
                  overflowWrap: 'break-word',
                  lineHeight: 1.06,
                  fontWeight: 900,
                  letterSpacing: 0,
                }}
              >
                Every unlocked ride brings you{' '}
                <Box component="span" sx={{ color: '#ffc928' }}>
                  closer to a booking.
                </Box>
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.76)', lineHeight: 1.7, fontWeight: 500, fontSize: { xs: 15, sm: 17 } }}>
                RideSaathi organizes ride requirements from authorized sources and verified contributors into qualified, credit-based rides so you earn more, spend less and drive more.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                {!isLoggedIn && (
                  <Button
                    component={RouterLink}
                    to="/signup"
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    sx={{ bgcolor: '#ffc928', color: '#07182d', fontWeight: 900, py: 1.35, width: { xs: '100%', sm: 'auto' }, '&:hover': { bgcolor: '#f3b51b' } }}
                  >
                    Start free with credits
                  </Button>
                )}
                <Button
                  component="a"
                  href="#how-it-works"
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.34)', py: 1.35, width: { xs: '100%', sm: 'auto' }, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.06)' } }}
                >
                  How it works
                </Button>
              </Stack>
              <Stack direction="row" flexWrap="wrap" sx={{ gap: { xs: 1, sm: 2.5 } }}>
                {['Authorized Sources', 'No Spam', '24x7 Support'].map((item) => (
                  <Stack key={item} direction="row" alignItems="center" spacing={0.75}>
                    <CheckCircleIcon sx={{ color: '#27c46b', fontSize: 17 }} />
                    <Typography variant="caption" color="rgba(255,255,255,0.82)" fontWeight={800}>
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2} sx={{ maxWidth: 430, width: '100%', mx: { xs: 'auto', md: 0 }, ml: { md: 'auto' } }}>
              <HeroRideCard from="Connaught Place" to="Delhi Airport" time="Today - 2:15 PM" price="₹320" />
              <HeroRideCard from="Sector 18, Noida" to="Chandigarh Sector 17" time="Tomorrow - 9:00 AM" price="₹660" muted />
              <Paper
                elevation={0}
                sx={{
                  p: 2.25,
                  borderRadius: 1.5,
                  bgcolor: '#ffd25d',
                  color: '#07182d',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  boxShadow: '0 18px 55px rgba(2,14,28,0.18)',
                }}
              >
                <LockIcon fontSize="small" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" fontWeight={900} display="block">
                    Available Credits
                  </Typography>
                  <Typography variant="caption" color="rgba(7,24,45,0.7)">
                    Credits for more rides
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={900} sx={{ flexShrink: 0 }}>Free credits</Typography>
              </Paper>
            </Stack>
          </Grid>
          </Grid>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          maxWidth: 980,
          mx: 'auto',
          width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)', md: 'auto' },
          mt: { xs: -5, md: -6 },
          position: 'relative',
          zIndex: 2,
          borderRadius: 2,
          border: '1px solid rgba(7,24,45,0.08)',
          boxShadow: '0 24px 70px rgba(7,24,45,0.11)',
          overflow: 'hidden',
        }}
      >
        <Grid container>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={stat.label}>
              <Box sx={{
                py: { xs: 2, sm: 2.5 },
                px: 1,
                minHeight: { xs: 82, sm: 'auto' },
                textAlign: 'center',
                borderRight: {
                  xs: index % 2 === 0 ? '1px solid rgba(7,24,45,0.08)' : 'none',
                  md: index < stats.length - 1 ? '1px solid rgba(7,24,45,0.12)' : 'none',
                },
                borderBottom: { xs: index < 2 ? '1px solid rgba(7,24,45,0.08)' : 'none', md: 'none' },
              }}>
                <Typography variant="h5" fontWeight={900} sx={{ fontSize: { xs: 20, sm: 24 } }}>{stat.value}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ fontSize: { xs: 10, sm: 12 } }}>{stat.label}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Box id="features" sx={{ pt: { xs: 8, md: 10 }, pb: { xs: 8, md: 11 }, bgcolor: '#ffffff' }}>
        <Stack alignItems="center" spacing={1} sx={{ ...containedSx(), mb: 5 }}>
          <Badge label="WHY DRIVERS LOVE US" />
          <Typography variant="h3" textAlign="center" sx={{ fontSize: { xs: 28, md: 36 } }}>
            Everything a taxi partner needs
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Powerful tools and features to help you book more rides.
          </Typography>
        </Stack>
        <Box sx={containedSx()}>
          <Grid
            container
            rowSpacing={2.5}
            columnSpacing={{ xs: 0, sm: 2.5 }}
            sx={gridContainerSx}
          >
            {features.map((feature) => (
              <Grid item xs={12} sm={6} md={3} key={feature.title}>
                <FeatureTile {...feature} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <Box
        id="how-it-works"
        sx={{
          py: { xs: 7, md: 9 },
          bgcolor: '#061b31',
          backgroundImage:
            'linear-gradient(135deg, rgba(6,27,49,1), rgba(8,35,63,1)), linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: 'auto, 68px 68px, 68px 68px',
        }}
      >
        <Stack alignItems="center" spacing={1} sx={{ ...containedSx(), mb: 6 }}>
          <Chip size="small" label="HOW IT WORKS" sx={{ bgcolor: 'rgba(255,201,40,0.12)', color: '#ffc928', border: '1px solid rgba(255,201,40,0.4)', fontSize: 10, fontWeight: 900 }} />
          <Typography variant="h3" color="white" textAlign="center" sx={{ fontSize: { xs: 27, md: 36 } }}>
            From sign-up to first booking in minutes
          </Typography>
        </Stack>
        <Box sx={containedSx()}>
          <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2 }} justifyContent="center" sx={gridContainerSx}>
            <Grid item xs={12} sm={6} md={3}>
              <Step n="01" icon={<AccountIcon />} title="Create account" body="Sign up in seconds with your phone number and city." />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Step n="02" icon={<FlashIcon />} title="Browse live rides" body="Review ride requirements from authorized sources and verified contributors." />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Step n="03" icon={<LockIcon />} title="Unlock ride" body="Use credits to unlock the full ride details and connect with the rider." />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Step n="04" icon={<CheckCircleIcon />} title="Confirm and earn" body="Confirm the ride, serve the customer and earn more." />
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box id="earnings" sx={{ py: { xs: 7, md: 11 }, bgcolor: '#ffffff' }}>
        <Box sx={containedSx()}>
        <Grid
          container
          rowSpacing={{ xs: 3, md: 5 }}
          columnSpacing={{ xs: 0, md: 5 }}
          alignItems="center"
          justifyContent="center"
          sx={gridContainerSx}
        >
          <Grid item xs={12} md={5}>
            <Stack spacing={2.25}>
              <Badge label="SEE BEFORE YOU COMMIT" />
              <Typography variant="h4" sx={{ fontSize: { xs: 22, md: 28 } }}>See the ride before you commit a credit</Typography>
              <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                View pickup, drop, time and price details before you unlock. No surprises. Just smarter decisions.
              </Typography>
              {['View full trip details', 'Know fare and estimated time', 'Choose only the rides that work for you', 'No hidden charges'].map((item) => (
                <Stack key={item} direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon sx={{ fontSize: 17, color: '#14a35b' }} />
                  <Typography variant="body2">{item}</Typography>
                </Stack>
              ))}
              <Button component={RouterLink} to={ridesPath} variant="outlined" endIcon={<ArrowForwardIcon />} sx={{ width: { xs: '100%', sm: 'fit-content' } }}>
                Explore live rides
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={3.5}>
            <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 1.5, bgcolor: '#f8fafc', borderColor: 'rgba(7,24,45,0.08)' }}>
              <Stack spacing={1.6}>
                <HeroRideCard from="Connaught Place" to="Delhi Airport" time="Today - 2:15 PM" price="₹320" />
                <HeroRideCard from="IGI Road, Gurgaon" to="Jaipur" time="Tomorrow - 9:00 AM" price="₹540" />
                <HeroRideCard from="Lajpat Nagar" to="Noida Sector 62" time="Today - 11:45 AM" price="₹420" muted />
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3.5} id="testimonials">
            <Stack spacing={2}>
              <Badge label="DRIVERS TRUST US" />
              <Typography variant="h4" sx={{ fontSize: { xs: 22, md: 28 } }}>Built for practical driver decisions</Typography>
              <Grid container rowSpacing={1.5} columnSpacing={{ xs: 0, sm: 1.5 }} sx={gridContainerSx}>
                {[
                  { title: 'Locked until useful', text: 'Contact and price stay hidden until a driver chooses to spend one credit.', avatar: '01' },
                  { title: 'Driver-owned filters', text: 'Pickup, drop and route matching stays personal to each driver account.', avatar: '02' },
                ].map((item) => (
                  <Grid item xs={12} sm={6} md={12} key={item.title}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, borderColor: 'rgba(7,24,45,0.09)', boxShadow: '0 14px 36px rgba(7,24,45,0.05)' }}>
                      <Stack spacing={1.25}>
                        <Stack direction="row" spacing={0.25}>
                          {[1, 2, 3, 4, 5].map((star) => <StarIcon key={star} sx={{ fontSize: 15, color: '#ffc928' }} />)}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" lineHeight={1.7}>
                          {item.text}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 30, height: 30, bgcolor: '#07182d', fontSize: 11 }}>{item.avatar}</Avatar>
                          <Box>
                            <Typography variant="caption" fontWeight={900} display="block">{item.title}</Typography>
                            <Typography variant="caption" color="text.secondary">RideSaathi workflow</Typography>
                          </Box>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Grid>
        </Grid>
        </Box>
      </Box>

      <Box id="pricing" sx={{ py: { xs: 7, md: 9 }, bgcolor: '#f5f7fb' }}>
        <Stack alignItems="center" spacing={1} sx={{ ...containedSx(), mb: 5 }}>
          <Badge label="CHOOSE YOUR PLAN" />
          <Typography variant="h3" textAlign="center" sx={{ fontSize: { xs: 27, md: 36 } }}>
            Pay only for the rides you unlock
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No hidden fees. Free rides available every month.
          </Typography>
        </Stack>
        <Box sx={{ ...containedSx(980), mb: 3 }}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 1.5,
              borderColor: '#ffc928',
              bgcolor: '#fffaf0',
              boxShadow: '0 14px 36px rgba(7,24,45,0.05)',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
              <Box>
                <Chip
                  size="small"
                  label="LAUNCH OFFER"
                  sx={{ bgcolor: '#ffc928', color: '#07182d', fontSize: 10, fontWeight: 900, mb: 1 }}
                />
                <Typography variant="h6" fontWeight={900} color="#07182d" sx={{ fontSize: { xs: 17, sm: 20 } }}>
                  First 100 drivers get {signupFreeCredits} free unlocked ride view access.
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Early users can view unlocked ride details while launch slots are available.
                </Typography>
              </Box>
              <Button component={RouterLink} to={isLoggedIn ? ridesPath : '/signup'} variant="contained" color="secondary" sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
                {isLoggedIn ? 'View rides' : 'Claim access'}
              </Button>
            </Stack>
          </Paper>
        </Box>
        <Box sx={containedSx(980)}>
          <Grid
            container
            rowSpacing={2.5}
            columnSpacing={{ xs: 0, sm: 2.5 }}
            justifyContent="center"
            sx={gridContainerSx}
          >
            {plans.map((plan) => {
              const isPopular = plan.slug === 'pro' || (!plans.some((p) => p.slug === 'pro') && plan.price === Math.max(...plans.map((p) => p.price)));
              const isFree = plan.slug === 'free';
              const features = plan.features && plan.features.length > 0
                ? plan.features
                : [
                    plan.monthlyLeadLimit === null
                      ? 'Unlimited ride unlocks/month'
                      : plan.monthlyLeadLimit === 0
                        ? 'Uses wallet credits'
                        : `${plan.monthlyLeadLimit} ride unlocks/month`,
                    ...(plan.topUpAllowed ? ['Top-up packs available'] : []),
                    ...(plan.canExport ? ['CSV export'] : []),
                    ...(plan.prioritySupport ? ['Priority support'] : []),
                  ];
              return (
                <Grid item xs={12} sm={6} md={4} key={plan.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: { xs: 2.25, sm: 2.5 },
                      borderRadius: 1.5,
                      borderColor: isPopular ? '#ffc928' : 'rgba(7,24,45,0.1)',
                      position: 'relative',
                      height: '100%',
                      boxShadow: isPopular ? '0 22px 58px rgba(7,24,45,0.11)' : '0 12px 32px rgba(7,24,45,0.045)',
                    }}
                  >
                    {isPopular && <Chip size="small" label="Most Popular" sx={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', bgcolor: '#ffc928', color: '#07182d' }} />}
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2" fontWeight={900}>{plan.name}</Typography>
                      <Typography variant="h3" fontWeight={900}>
                        {isFree ? 'Free' : `₹${plan.price}`}
                        {!isFree && <Typography component="span" variant="caption" color="text.secondary"> /mo</Typography>}
                      </Typography>
                      {plan.description && (
                        <Typography variant="caption" color="text.secondary">{plan.description}</Typography>
                      )}
                      <Stack spacing={0.8}>
                        {features.map((item) => (
                          <Stack key={item} direction="row" spacing={0.8} alignItems="center">
                            <CheckCircleIcon sx={{ fontSize: 15, color: '#14a35b', flexShrink: 0 }} />
                            <Typography variant="caption">{item}</Typography>
                          </Stack>
                        ))}
                      </Stack>
                      <Button
                        component={RouterLink}
                        to={isLoggedIn ? '/plans' : '/signup'}
                        variant={isPopular ? 'contained' : 'outlined'}
                        color={isPopular ? 'secondary' : 'primary'}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        {isLoggedIn ? 'Get this plan' : 'Get started'}
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>

      <Box sx={{ bgcolor: '#ffffff', pb: { xs: 7, md: 9 } }}>
        <Box sx={containedSx()}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 1.5,
              color: 'white',
              bgcolor: '#061b31',
              backgroundImage: 'linear-gradient(135deg, #061b31, #0b3156)',
            }}
          >
            <Grid container rowSpacing={2} columnSpacing={{ xs: 0, md: 2 }} alignItems="center" sx={gridContainerSx}>
              <Grid item xs={12} md={7}>
                <Stack spacing={1}>
                  <Chip size="small" label="LIMITED TIME OFFER" sx={{ width: 'fit-content', bgcolor: 'rgba(255,201,40,0.15)', color: '#ffc928', fontSize: 10 }} />
                  <Typography variant="h4" color="white" sx={{ fontSize: { xs: 20, md: 28 } }}>Ride unlocks, free every month.</Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.72)">
                    Join taxi partners who take smarter ride decisions with RideSaathi.
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent={{ md: 'flex-end' }}>
                  {!isLoggedIn && (
                    <Button component={RouterLink} to="/signup" variant="contained" color="secondary" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      Create free account
                    </Button>
                  )}
                  <Button component={RouterLink} to={ridesPath} variant="outlined" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.32)', width: { xs: '100%', sm: 'auto' } }}>
                    Browse live rides
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Landing;
